import React, { useEffect, useState, useRef, useReducer } from 'react';
import { initialState, reducer } from './dialogReducer';
import { fetchDialogs } from '../../api/fetchDialogs';
import { playUserAudio, toggleTranslation } from '../../utils/dialogUtils';
import { useAppConfig } from '../../context/AppConfigContext';
import { useAudioRecorderGeneral } from '../../hooks/useAudioRecorderGeneral';
import { useAssistantDialogFeedback } from '../../hooks/useAssistantFeedback';

import AudioTimeEnded from '../../components/AudioTimeEnded/AudioTimeEnded';
import NewUserHint from '../../components/NewUserHint/NewUserHint';

import styles from './DialogScreen.module.css';
import { DialogOptions } from './DialogOptions';
import { DialogHistory } from './DialogHistory';

import { Capacitor } from '@capacitor/core';
import waitGif from '../wait100.gif';

const DialogScreen = ({ dialogId, onBack, userId, onGoPremium = null }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    currentStep,
    steps,
    intro,
    loading,
    error,
    audioBlob,
    recognizedText,
    matchFailed,
    showIntro,
    showTranslation,
    attemptCount,
    matchedOptionIndex,
    assistantSuccess,
    dialogHistory,
    audioTimeEnded,
  } = state;

  const step = steps[currentStep - 1] || {};
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [showIntroText, setShowIntroText] = useState(false);
  const buttonRef = useRef(null);
  const { isTelegramWebApp, t, baseLanguage, targetLanguage, isAndroidApp, site } = useAppConfig();
  const isNative = Capacitor.isNativePlatform();
  const combinedIsAndroidApp = isAndroidApp || isNative;
  const [manualRecordingStarted, setManualRecordingStarted] = useState(false);
  const [newUserHintClosed, setNewUserHintClosed] = useState(false);

  const recorder = useAudioRecorderGeneral({
    userId,
    stepId: step.id,
    contextId: dialogId,
    source: 'dialog',
    dispatch,
    step: { ...step, dialogId },
    state,
    buttonRef,
    attemptCount,
    isTelegramWebApp,
  });

  useAssistantDialogFeedback({
    audioBlob,
    recognizedText,
    matchFailed,
    assistantSuccess,
    dispatch,
    baseLanguage,
    targetLanguage,
  });

  useEffect(() => {
    if (!newUserHintClosed) return;
    if (intro?.introAudio) {
      setIsAudioPlaying(true);

      const audioSrc = `${combinedIsAndroidApp ? site : ''}${intro.introAudio}`;
      const audio = playUserAudio(
        audioSrc,
        dispatch,
        () => {
          setIsAudioPlaying(false);
          setTimeout(() => dispatch({ type: 'SET_SHOW_INTRO', payload: false }), 2000);
        },
        () => {
          setIsAudioPlaying(false);
        }
      );
      return () => {
        audio.pause();
        audio.currentTime = 0;
        setIsAudioPlaying(false);
      };
    }
  }, [intro?.introAudio, dialogId, userId, dispatch, newUserHintClosed, combinedIsAndroidApp, site]);

  useEffect(() => {
    if (!showIntro && step.assistantAudioUrl) {
      setIsAudioPlaying(true);

      const audioSrc = `${combinedIsAndroidApp ? site : ''}${step.assistantAudioUrl}`;
      const audio = playUserAudio(
        audioSrc,
        dispatch,
        () => setIsAudioPlaying(false),
        () => setIsAudioPlaying(false)
      );

      return () => {
        audio.pause();
        audio.currentTime = 0;
        setIsAudioPlaying(false);
      };
    }
  }, [showIntro, step.assistantAudioUrl, dialogId, userId, step.id, dispatch, combinedIsAndroidApp, site]);

  useEffect(() => {
    if (dialogId && userId) {
      fetchDialogs({ dialogId, userId, dispatch });
    }
  }, [dialogId, userId]);

  useEffect(() => {
    if (!recorder.isRecording && !recorder.isProcessing) {
      setManualRecordingStarted(false);
    }
  }, [recorder.isRecording, recorder.isProcessing]);

  const toggleIntroText = () => {
    setShowIntroText(!showIntroText);
  };

  if (state.endDialog && dialogHistory?.length > 0) {
    return (
      <div className={styles.dialogScreen}>
        <h1 className={styles.dialogTitle}>{t.dialog.dialogFinishedTitle}</h1>
        <DialogHistory
          dialogHistory={dialogHistory}
          showTranslation={showTranslation}
          dispatch={dispatch}
          combinedIsAndroidApp={combinedIsAndroidApp}
          site={site}
        />
        <button onClick={() => onBack(5 * step.totalSteps, step.totalSteps)} className={styles.backButton}>
          {t.dialog.backButton}
        </button>
      </div>
    );
  }

  if (!newUserHintClosed) {
    return <NewUserHint source="dialog" onClose={() => setNewUserHintClosed(true)} />;
  }
  if (loading) return <div className={styles.loading}>Loading...</div>;
  if (!recorder.isSupported) return <div className={styles.error}>Audio recording is not supported</div>;
  if (error && matchFailed && !recognizedText && attemptCount > 0 && !recorder.isProcessing) {
    return <div className={styles.error}>{error}</div>;
  }
  if (audioTimeEnded) {
    return <AudioTimeEnded onBack={() => onBack(5 * step.id, step.id)} onGoPremium={onGoPremium} />;
  }

  return (
    <div className={styles.dialogScreen}>
      <h1 className={styles.dialogTitle}>
        {t.dialog.dialogTopic
          .replace('{topic}', step.topic)
          .replace('{current}', step.id)
          .replace('{total}', step.totalSteps)}
      </h1>

      <div className={styles.introBlock}>
        {intro?.introImage && (
          <img
            src={`${combinedIsAndroidApp ? site : ''}${intro.introImage}`}
            alt="Dialogue scene"
            className={styles.introImage}
          />
        )}
        <div className={styles.introTextContainer} onClick={() => toggleTranslation(dispatch, `assistant-${step.id || 'temp'}`)}>
          {showIntro ? (
            <div>
              <p onClick={toggleIntroText} style={{ cursor: 'pointer' }}>
                {intro.introTranslate}
              </p>
              {showIntroText && <p className={styles.translation}>{intro.introText || ''}</p>}
            </div>
          ) : (
            <>
              {step.assistantAudioUrl && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsAudioPlaying(true);
                    const audioSrc = `${combinedIsAndroidApp ? site : ''}${step.assistantAudioUrl}`;
                    playUserAudio(
                      audioSrc, 
                      dispatch,
                      () => setIsAudioPlaying(false),
                      () => setIsAudioPlaying(false)
                    );
                  }}
                  className={styles.audioButton}
                  aria-label="Play"
                >
                  <svg viewBox="0 0 24 24" className={styles.audioIcon}>
                    <path d="M3 10v4h4l5 5V5L7 10H3zm13.5 2c0-1.77-1.02-3.29-2.5-4.03v8.06A4.984 4.984 0 0 0 16.5 12zm2.5 0c0 3.04-1.64 5.64-4.06 7.03l1.43 1.43A9.969 9.969 0 0 0 22 12c0-3.73-2.05-6.99-5.13-8.64l-1.43 1.43A7.97 7.97 0 0 1 19 12z" />
                  </svg>
                </button>
              )}
              <p>{step.assistantText}</p>
              {showTranslation[`assistant-${step.id || 'temp'}`] && (
                <p className={styles.translation}>{step.assistantTextTranslate}</p>
              )}
            </>
          )}
        </div>
      </div>

      <DialogOptions
        options={step.options}
        matchedOptionIndex={matchedOptionIndex}
        showTranslation={showTranslation}
        dispatch={dispatch}
        combinedIsAndroidApp={combinedIsAndroidApp}
        site={site}
      />

      {step.id && !audioBlob && (
        <div className={styles.recorderContainer}>
          <div className={styles.assistantLine}>
            <button
              ref={buttonRef}
              type="button"
              className={`${styles.recordButton} ${recorder.isRecording ? styles.recording : ''}`}
              disabled={
                !recorder.permissionGranted ||
                isAudioPlaying ||
                recorder.isProcessing ||
                matchedOptionIndex !== null
              }
              onMouseDown={(e) => {
                e.preventDefault();
                recorder.handleStartRecording(step?.options || []);
                setManualRecordingStarted(true);
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                recorder.handleStartRecording(step?.options || []);
                setManualRecordingStarted(true);
              }}
              onMouseUp={(e) => {
                e.preventDefault();
                recorder.handleStopRecording();
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                recorder.handleStopRecording();
              }}
            >
              <svg viewBox="0 0 24 24">
                <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 14 0h-2zM11 21h2v-2h-2v2z" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {manualRecordingStarted && recorder.isProcessing && (
        <div className={styles.loading}>
          <img src={waitGif} alt="Loading..." />
        </div>
      )}
    </div>
  );
};

export default DialogScreen;
